import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import { useNavigate } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toMarketplaceSearch } from "@/features/marketplace/lib/marketplace-search";
import { useRevealTarget } from "@/hooks/useRevealTarget";
import { useRetryOrder, useUpdateOrderScriptFulfillment } from "@/lib/queries";

import { useCanEditMarketplaceOrderFulfillment } from "../../admin/hooks/useCanEditMarketplaceOrderFulfillment";
import { getFulfillmentStatusLabel } from "../../lib/fulfillment-status-label";
import { localTodayYmd } from "../../lib/order-deadline";
import type { OrderLineTypeFilterOption } from "../../lib/order-line-kind";
import { exportInProgressOrdersXlsx } from "../../lib/orders-export";
import { resolveRevisionDeliverableId } from "../../lib/revision-deliverable";
import {
  adjacentScriptTarget,
  loadPanelWidth,
  panelSearchPatch,
  panelTargetFromSearch,
  resolvePanelTarget,
  type OrdersPanelTarget,
} from "../../lib/orders-detail-panel";
import {
  DEFAULT_ORDERS_LIST_QUERY,
  bulkFulfillmentOptions,
  clearOrdersListFilters,
  describeOrderAttention,
  filterOrders,
  groupOrders,
  isRevealOrderHidden,
  needsAttention,
  planBulkStatusChange,
  sortOrders,
  vendorOptions,
  type DeadlineGroupKey,
  type OrdersListQuery,
  type ScriptSelection,
} from "../../lib/orders-list-model";
import type { MarketplaceOrder } from "../../types/marketplace.types";
import { useBulkStatusChange } from "./useBulkStatusChange";

export type OrdersListVariant = "in-production" | "history" | "archive";
export type OrdersListNavigationMode = "marketplace" | "embedded";
export type OrdersListChrome = "marketplace" | "hub";

export interface OrdersListProps {
  orders: MarketplaceOrder[];
  tenantId: string;
  variant: OrdersListVariant;
  navigationMode?: OrdersListNavigationMode;
  /** Hub chrome restyles the toolbar, foldouts, and drawer. Marketplace stays the default. */
  chrome?: OrdersListChrome;
  stateScope?: string;
  highlightOrderId?: string;
  highlightScriptId?: string;
  /** Controls the Type filter when the parent sends it to the server; pass with `onLineTypeChange`. */
  lineType?: OrderLineTypeFilterOption;
  onLineTypeChange?: (lineType: OrderLineTypeFilterOption) => void;
}

const DEADLINE_GROUP_LABEL_KEY: Record<DeadlineGroupKey, string> = {
  overdue: "marketplace.orders.list.deadlineGroups.overdue",
  today: "marketplace.orders.list.deadlineGroups.today",
  thisWeek: "marketplace.orders.list.deadlineGroups.thisWeek",
  later: "marketplace.orders.list.deadlineGroups.later",
  unscheduled: "marketplace.orders.list.deadlineGroups.unscheduled",
};

const EMPTY_SELECTION: ScriptSelection = new Set();

function expandedStorageKey(
  tenantId: string,
  variant: OrdersListVariant,
  stateScope: string | undefined,
): string {
  const scopeSegment = stateScope ? `:${stateScope}` : "";
  return `marketplace:orders:${tenantId}:${variant}${scopeSegment}:expanded`;
}

function loadExpandedOrderIds(
  storageKey: string,
  highlightOrderId: string | undefined,
  highlightScriptId: string | undefined,
  orders: readonly MarketplaceOrder[],
): ReadonlySet<string> {
  const fallback = new Set(highlightOrderId ? [highlightOrderId] : []);
  if (highlightScriptId) {
    const owner = orders.find((order) =>
      order.scripts.some((script) => script.id === highlightScriptId),
    );
    if (owner) fallback.add(owner.id);
  }
  if (typeof window === "undefined") return fallback;
  try {
    const stored = JSON.parse(
      window.sessionStorage.getItem(storageKey) ?? "[]",
    );
    if (!Array.isArray(stored)) return fallback;
    for (const id of stored) {
      if (typeof id === "string") fallback.add(id);
    }
  } catch {
    // A corrupt preference should never block the orders view.
  }
  return fallback;
}

function isEditableTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable)
  );
}

export function useOrdersListController({
  orders,
  tenantId,
  variant,
  navigationMode = "marketplace",
  chrome = "marketplace",
  stateScope,
  highlightOrderId,
  highlightScriptId,
  lineType,
  onLineTypeChange,
}: OrdersListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canEdit = useCanEditMarketplaceOrderFulfillment();
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const todayYmd = localTodayYmd();
  const storageKey = expandedStorageKey(tenantId, variant, stateScope);

  const [localQuery, setLocalQuery] = useState<OrdersListQuery>(() => ({
    ...DEFAULT_ORDERS_LIST_QUERY,
    sortBy: variant === "in-production" ? "deadline" : "createdAt",
    sortDirection: variant === "in-production" ? "asc" : "desc",
  }));
  const query = useMemo(
    () => (lineType === undefined ? localQuery : { ...localQuery, lineType }),
    [lineType, localQuery],
  );
  const setQuery = useCallback(
    (next: OrdersListQuery) => {
      setLocalQuery(next);
      if (next.lineType !== query.lineType) onLineTypeChange?.(next.lineType);
    },
    [onLineTypeChange, query.lineType],
  );
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() =>
    loadExpandedOrderIds(
      storageKey,
      highlightOrderId,
      highlightScriptId,
      orders,
    ),
  );
  const [selection, setSelection] = useState<ScriptSelection>(EMPTY_SELECTION);
  const [activeRowId, setActiveRowId] = useState<string | null>(
    highlightOrderId ? `order:${highlightOrderId}` : null,
  );
  const [panelTarget, setPanelTarget] = useState<OrdersPanelTarget | null>(() =>
    resolvePanelTarget(
      panelTargetFromSearch({
        order: highlightOrderId,
        script: highlightScriptId,
      }),
      orders,
    ),
  );
  const [panelWidth, setPanelWidth] = useState(loadPanelWidth);
  const [exportPending, setExportPending] = useState(false);
  const consumedRevealScriptRef = useRef<string | null>(null);
  // The drawer writes its own target to the URL, so only an unseen target is a deep link.
  const handledRevealTargetRef = useRef<string | null>(null);

  const retryOrder = useRetryOrder();
  const updateScript = useUpdateOrderScriptFulfillment();
  const bulk = useBulkStatusChange(tenantId);
  const canInteract = canEdit && !bulk.applying;

  const visibleOrders = useMemo(
    () =>
      sortOrders(
        filterOrders(orders, query, todayYmd),
        query.sortBy,
        query.sortDirection,
      ),
    [orders, query, todayYmd],
  );
  const groups = useMemo(
    () => groupOrders(visibleOrders, query.groupBy, todayYmd),
    [visibleOrders, query.groupBy, todayYmd],
  );
  const vendors = useMemo(() => vendorOptions(orders), [orders]);
  const attentionCount = useMemo(
    () =>
      orders.filter((order) =>
        needsAttention(describeOrderAttention(order, todayYmd)),
      ).length,
    [orders, todayYmd],
  );

  const visibleScriptIds = useMemo(
    () =>
      visibleOrders.flatMap((order) =>
        order.scripts.map((script) => script.id),
      ),
    [visibleOrders],
  );
  const bulkStatusOptions = useMemo(
    () =>
      bulkFulfillmentOptions(
        planBulkStatusChange(visibleOrders, selection),
      ),
    [selection, visibleOrders],
  );
  const revealTargetId = highlightScriptId ?? highlightOrderId;
  const revealOrder = useMemo(
    () =>
      orders.find(
        (order) =>
          order.id === highlightOrderId ||
          order.scripts.some((script) => script.id === highlightScriptId),
      ) ?? null,
    [highlightOrderId, highlightScriptId, orders],
  );

  useEffect(() => {
    const requestedTarget = panelTargetFromSearch({
      order: highlightOrderId,
      script: highlightScriptId,
    });
    if (!requestedTarget) {
      setPanelTarget(null);
      return;
    }

    const shouldPreserveConsumedScript =
      Boolean(consumedRevealScriptRef.current) && !highlightScriptId;
    if (shouldPreserveConsumedScript) {
      consumedRevealScriptRef.current = null;
    }

    const resolved = resolvePanelTarget(requestedTarget, orders);
    setPanelTarget((current) => {
      if (
        shouldPreserveConsumedScript &&
        current?.kind === "script" &&
        current.orderId === highlightOrderId
      ) {
        return current;
      }
      if (
        current?.kind === resolved?.kind &&
        current?.orderId === resolved?.orderId &&
        (current?.kind !== "script" ||
          (resolved?.kind === "script" &&
            current.scriptId === resolved.scriptId))
      ) {
        return current;
      }
      return resolved;
    });
    if (resolved && !shouldPreserveConsumedScript) {
      setActiveRowId(
        resolved.kind === "script"
          ? `script:${resolved.scriptId}`
          : `order:${resolved.orderId}`,
      );
    }
    if (!revealOrder) return;
    setExpandedIds((current) => {
      if (current.has(revealOrder.id)) return current;
      const next = new Set(current);
      next.add(revealOrder.id);
      return next;
    });
  }, [
    highlightOrderId,
    highlightScriptId,
    orders,
    revealOrder,
  ]);

  useEffect(() => {
    if (!revealTargetId || handledRevealTargetRef.current === revealTargetId) {
      return;
    }
    handledRevealTargetRef.current = revealTargetId;
    if (
      !isRevealOrderHidden({
        query,
        hasRevealTarget: Boolean(revealTargetId),
        revealOrderId: revealOrder?.id ?? null,
        visibleOrders,
      })
    ) {
      return;
    }
    setQuery(clearOrdersListFilters(query));
  }, [query, revealOrder, revealTargetId, setQuery, visibleOrders]);

  const revealAnnouncement = useMemo(() => {
    if (!revealOrder) return "";
    const scriptIndex = revealOrder.scripts.findIndex(
      (script) => script.id === highlightScriptId,
    );
    return scriptIndex >= 0
      ? t("marketplace.orders.list.reveal.scriptAnnouncement", {
          defaultValue: "Showing script {{number}} of order {{order}}",
          number: scriptIndex + 1,
          order: revealOrder.vendor.name,
        })
      : t("marketplace.orders.list.reveal.orderAnnouncement", {
          defaultValue: "Showing order {{order}}",
          order: revealOrder.vendor.name,
        });
  }, [highlightScriptId, revealOrder, t]);

  const { liveAnnouncement } = useRevealTarget({
    targetId: revealTargetId,
    isReady: true,
    getElement: () => {
      if (!revealTargetId) return null;
      const attribute = highlightScriptId
        ? "data-order-script-id"
        : "data-order-id";
      return (
        [...(listRef.current?.querySelectorAll<HTMLElement>(`[${attribute}]`) ?? [])].find(
          (element) => element.getAttribute(attribute) === revealTargetId,
        ) ?? null
      );
    },
    announcement: revealAnnouncement,
    onReveal: () => {
      if (!highlightScriptId || navigationMode === "embedded") return;
      consumedRevealScriptRef.current = highlightScriptId;
      handledRevealTargetRef.current = highlightOrderId ?? revealOrder?.id ?? null;
      void navigate({
        to: "/marketplace",
        search: {
          tab: variant,
          order: highlightOrderId ?? revealOrder?.id,
        },
        replace: true,
      });
    },
    onTimeout: () => {
      toast.error(
        t("marketplace.orders.list.reveal.notFound", {
          defaultValue: "Couldn't find this script — it may have moved to History",
        }),
        {
          action: {
            label: t("marketplace.orders.list.reveal.viewHistory", {
              defaultValue: "View History",
            }),
            onClick: () => {
              void navigate({
                to: "/marketplace",
                search: { tab: "history" },
              });
            },
          },
        },
      );
    },
  });
  const selectedVisibleCount = visibleScriptIds.filter((id) =>
    selection.has(id),
  ).length;
  const allVisibleSelected =
    visibleScriptIds.length > 0 &&
    selectedVisibleCount === visibleScriptIds.length;

  useEffect(() => {
    setSelection((current) => {
      const next = new Set(
        [...current].filter((id) => visibleScriptIds.includes(id)),
      );
      return next.size === current.size ? current : next;
    });
  }, [visibleScriptIds]);

  const pendingScriptIds = useMemo(() => {
    const ids = new Set<string>();
    if (updateScript.isPending && updateScript.variables)
      ids.add(updateScript.variables.orderScriptId);
    if (bulk.applying) for (const id of selection) ids.add(id);
    return ids;
  }, [
    updateScript.isPending,
    updateScript.variables,
    bulk.applying,
    selection,
  ]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        storageKey,
        JSON.stringify([...expandedIds]),
      );
    } catch {
      // Storage can be unavailable in private browsing; expansion still works in memory.
    }
  }, [expandedIds, storageKey]);

  const toggleExpanded = useCallback((orderId: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelection(EMPTY_SELECTION), []);

  const toggleAllVisible = () => {
    setSelection((current) => {
      const next = new Set(current);
      if (allVisibleSelected) visibleScriptIds.forEach((id) => next.delete(id));
      else visibleScriptIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleScriptStatusChange = (
    order: MarketplaceOrder,
    scriptId: string,
    next: MarketplaceFulfillmentStatus,
  ) => {
    const script = order.scripts.find((row) => row.id === scriptId);
    if (!script) return;
    updateScript.mutate({
      orderId: order.id,
      orderScriptId: scriptId,
      tenantId,
      fulfillmentStatus: next,
      revisionNote: next === "SENT_FOR_REVISION" ? undefined : null,
      deliverableId:
        next === "SENT_FOR_REVISION"
          ? resolveRevisionDeliverableId(script, null)
          : undefined,
    });
  };

  const handleBulkStatus = async (status: MarketplaceFulfillmentStatus) => {
    const ok = await bulk.apply(visibleOrders, selection, status);
    if (ok) clearSelection();
  };

  const handleExportExcel = async () => {
    if (exportPending || visibleOrders.length === 0) return;
    setExportPending(true);
    try {
      const count = await exportInProgressOrdersXlsx(visibleOrders, t);
      if (count === 0) return;
      toast.success(t("marketplace.orders.list.export.success", { count }));
    } catch {
      toast.error(
        t("marketplace.orders.list.export.error", {
          defaultValue: "Export failed. Please try again.",
        }),
      );
    } finally {
      setExportPending(false);
    }
  };

  const openPanel = useCallback(
    (target: OrdersPanelTarget | null) => {
      const resolved = resolvePanelTarget(target, orders);
      setPanelTarget(resolved);
      if (resolved) {
        setExpandedIds((current) => {
          if (current.has(resolved.orderId)) return current;
          const next = new Set(current);
          next.add(resolved.orderId);
          return next;
        });
        setActiveRowId(
          resolved.kind === "script"
            ? `script:${resolved.scriptId}`
            : `order:${resolved.orderId}`,
        );
      }
      const patch = panelSearchPatch(resolved);
      if (navigationMode === "embedded") return;
      handledRevealTargetRef.current = patch.script ?? patch.order ?? null;
      void navigate({
        to: "/marketplace",
        search: (previous) => ({
          ...toMarketplaceSearch(previous),
          tab: variant,
          order: patch.order,
          script: patch.script,
        }),
        replace: true,
      });
    },
    [navigate, navigationMode, orders, variant],
  );

  useEffect(() => {
    const resolved = resolvePanelTarget(panelTarget, orders);
    const order = resolved
      ? (orders.find((candidate) => candidate.id === resolved.orderId) ?? null)
      : null;
    if (!resolved || !order) return;

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (isEditableTarget(event.target)) return;
      if (
        (event.key === "ArrowLeft" || event.key === "ArrowRight") &&
        event.target instanceof HTMLElement &&
        event.target.closest('[role="separator"]')
      ) {
        return;
      }
      if (
        document.querySelector(
          '[data-state="open"][data-slot="dropdown-menu-content"], [data-state="open"][data-slot="popover-content"]',
        )
      ) {
        return;
      }
      const foreignDialogOpen = [
        ...document.querySelectorAll('[data-state="open"][role="dialog"]'),
      ].some((node) => !node.hasAttribute("data-orders-panel"));
      if (foreignDialogOpen) return;
      if (event.key === "Escape") {
        event.preventDefault();
        openPanel(null);
        return;
      }
      if (
        !["j", "J", "k", "K", "ArrowLeft", "ArrowRight"].includes(event.key)
      ) {
        return;
      }
      const delta: -1 | 1 =
        event.key === "j" || event.key === "J" || event.key === "ArrowRight"
          ? 1
          : -1;
      const next = adjacentScriptTarget(resolved, order.scripts, delta);
      if (!next) return;
      event.preventDefault();
      openPanel(next);
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [openPanel, orders, panelTarget]);

  const resolvedPanel = resolvePanelTarget(panelTarget, orders);
  const panelOrder = resolvedPanel
    ? (orders.find((order) => order.id === resolvedPanel.orderId) ?? null)
    : null;
  const panelScript =
    resolvedPanel?.kind === "script" && panelOrder
      ? (panelOrder.scripts.find((row) => row.id === resolvedPanel.scriptId) ??
        null)
      : null;
  const treeRowIds = useMemo(
    () =>
      visibleOrders.flatMap((order) => [
        `order:${order.id}`,
        ...(expandedIds.has(order.id)
          ? order.scripts.map((script) => `script:${script.id}`)
          : []),
      ]),
    [expandedIds, visibleOrders],
  );

  /** Roving focus between order rows; Escape closes the panel then clears selection; "/" jumps to search. */
  const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      if (resolvedPanel) {
        event.preventDefault();
        openPanel(null);
        return;
      }
      if (selection.size > 0) {
        event.preventDefault();
        clearSelection();
      }
      return;
    }
    if (isEditableTarget(event.target)) return;
    if (event.key === "/") {
      event.preventDefault();
      searchInputRef.current?.focus();
      return;
    }
    if (event.defaultPrevented || !(event.target instanceof HTMLElement))
      return;
    const currentRow = event.target.closest<HTMLElement>("[data-tree-row]");
    if (!currentRow || event.target !== currentRow) return;
    if (
      ![
        "ArrowDown",
        "ArrowUp",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(event.key)
    ) {
      return;
    }
    const rows = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>("[data-tree-row]") ?? [],
    );
    if (rows.length === 0) return;
    const currentIndex = rows.indexOf(currentRow);
    if (event.key === "ArrowRight") {
      const nextRow = rows[currentIndex + 1];
      if (
        currentRow.hasAttribute("data-order-row") &&
        currentRow.getAttribute("aria-expanded") === "true" &&
        nextRow?.hasAttribute("data-script-row")
      ) {
        event.preventDefault();
        nextRow.focus();
      }
      return;
    }
    if (event.key === "ArrowLeft") {
      if (!currentRow.hasAttribute("data-script-row")) return;
      const parentRow = rows
        .slice(0, currentIndex)
        .reverse()
        .find((row) => row.hasAttribute("data-order-row"));
      if (parentRow) {
        event.preventDefault();
        parentRow.focus();
      }
      return;
    }
    let nextIndex: number;
    switch (event.key) {
      case "ArrowDown":
        nextIndex =
          currentIndex < 0 ? 0 : Math.min(rows.length - 1, currentIndex + 1);
        break;
      case "ArrowUp":
        nextIndex =
          currentIndex < 0 ? rows.length - 1 : Math.max(0, currentIndex - 1);
        break;
      case "Home":
        nextIndex = 0;
        break;
      default:
        nextIndex = rows.length - 1;
    }
    event.preventDefault();
    rows[nextIndex]?.focus();
  };

  const rovingActiveId =
    activeRowId && treeRowIds.includes(activeRowId)
      ? activeRowId
      : (treeRowIds[0] ?? null);

  const groupLabel = (key: string): string => {
    switch (query.groupBy) {
      case "vendor":
        return vendors.find((vendor) => vendor.id === key)?.name ?? key;
      case "status":
        return getFulfillmentStatusLabel(
          t,
          key as MarketplaceFulfillmentStatus,
          "client",
        );
      case "deadline":
        return t(DEADLINE_GROUP_LABEL_KEY[key as DeadlineGroupKey]);
      case "none":
        return "";
    }
  };

  return {
    t,
    navigate,
    canEdit,
    listRef,
    searchInputRef,
    todayYmd,
    query,
    setQuery,
    expandedIds,
    selection,
    setSelection,
    setActiveRowId,
    panelWidth,
    setPanelWidth,
    exportPending,
    retryOrder,
    bulk,
    canInteract,
    visibleOrders,
    groups,
    vendors,
    attentionCount,
    bulkStatusOptions,
    liveAnnouncement,
    selectedVisibleCount,
    allVisibleSelected,
    pendingScriptIds,
    toggleExpanded,
    clearSelection,
    toggleAllVisible,
    handleScriptStatusChange,
    handleBulkStatus,
    handleExportExcel,
    openPanel,
    resolvedPanel,
    panelOrder,
    panelScript,
    treeRowIds,
    handleListKeyDown,
    rovingActiveId,
    groupLabel,
    tenantId,
    variant,
    chrome,
    orders,
  };
}
